
// src/pages/AdminServices.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  subscribeCategories,
  createCategory,
  updateCategory,
  setCategoryActive,
  deleteCategory,
  subscribeHomeServices,
  createHomeService,
  updateHomeService,
  setHomeServiceActive,
  deleteHomeService,
} from "../lib/servicesApi";

const AdminServices = () => {
  /* ---------- Categories ---------- */
  const [cats, setCats]       = useState([]);
  const [catError, setCatError] = useState(null);
  const [catForm, setCatForm] = useState({ key: "", label: "", active: true });
  const [catSaving, setCatSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeCategories(setCats, setCatError);
    return () => unsub();
  }, []);

  const onCatChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCatForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onCatCreate = async (e) => {
    e.preventDefault();
    if (!catForm.key) { setCatError(new Error("Category key is required")); return; }
    setCatSaving(true);
    try {
      await createCategory(catForm);
      setCatForm({ key: "", label: "", active: true });
    } catch (err) {
      setCatError(err);
    } finally {
      setCatSaving(false);
    }
  };

  /* ---------- Services ---------- */
  const [filterCat, setFilterCat] = useState("all");
  const [svcList, setSvcList]     = useState([]);
  const [svcError, setSvcError]   = useState(null);
  const [svcForm, setSvcForm]     = useState({
    category: "",
    type: "",
    title: "",
    desc: "",
    icon: "bi-tools",
    price: "",
    active: true,
  });
  const [svcSaving, setSvcSaving] = useState(false);

  useEffect(() => {
    const unsub = subscribeHomeServices(
      setSvcList,
      setSvcError,
      { category: filterCat === "all" ? undefined : filterCat }
    );
    return () => unsub();
  }, [filterCat]);

  // default category in service form: first active cat
  useEffect(() => {
    if (!svcForm.category) {
      const firstActive = cats.find((c) => c.active);
      if (firstActive) setSvcForm((f) => ({ ...f, category: firstActive.key }));
    }
  }, [cats, svcForm.category]);

  const onSvcChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSvcForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSvcCreate = async (e) => {
    e.preventDefault();
    if (!svcForm.category || !svcForm.type || !svcForm.title || !svcForm.price) {
      setSvcError(new Error("Please fill category, type, title and price."));
      return;
    }
    setSvcSaving(true);
    try {
      await createHomeService(svcForm);
      setSvcForm({
        category: cats.find((c) => c.active)?.key || "",
        type: "",
        title: "",
        desc: "",
        icon: "bi-tools",
        price: "",
        active: true,
      });
    } catch (err) {
      setSvcError(err);
    } finally {
      setSvcSaving(false);
    }
  };

  const activeCats = useMemo(() => cats.filter((c) => c.active), [cats]);

  return (
    <section>
      <h3 className="mb-3">Admin — Categories & Services</h3>

      {/* Categories panel */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h5 className="card-title">Add Category</h5>
          {catError && <div className="alert alert-danger">{catError.message}</div>}
          <form onSubmit={onCatCreate}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="key">Key (slug)</label>
                <input
                  id="key"
                  name="key"
                  className="form-control"
                  placeholder="e.g., cleaning"
                  value={catForm.key}
                  onChange={onCatChange}
                  required
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="label">Label (display)</label>
                <input
                  id="label"
                  name="label"
                  className="form-control"
                  placeholder="e.g., Cleaning"
                  value={catForm.label}
                  onChange={onCatChange}
                />
              </div>
              <div className="col-12 col-md-4 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={catForm.active}
                    onChange={onCatChange}
                  />
                  <label className="form-check-label" htmlFor="active">Active</label>
                </div>
              </div>
              <div className="col-12">
                <button className="btn btn-primary" type="submit" disabled={catSaving}>
                  {catSaving ? "Saving..." : "Add Category"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Manage Categories */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h5 className="card-title">Categories ({cats.length})</h5>
          {cats.length === 0 ? (
            <p className="text-muted">No categories yet. Add one above.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Label</th>
                    <th>Active</th>
                    <th style={{ width: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cats.map((c) => (
                    <tr key={c.id}>
                      <td><code>{c.key}</code></td>
                      <td>{c.label}</td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`catactive-${c.id}`}
                            checked={!!c.active}
                            onChange={(e) => setCategoryActive(c.id, e.target.checked)}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-outline-teal btn-sm"
                            type="button"
                            onClick={async () => {
                              const newLabel = window.prompt("New label:", c.label);
                              if (newLabel != null && newLabel.trim() !== "") {
                                await updateCategory(c.id, { label: newLabel.trim() });
                              }
                            }}
                          >
                            Rename
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            type="button"
                            onClick={async () => {
                              const ok = window.confirm(`Delete category "${c.label}"?`);
                              if (ok) await deleteCategory(c.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Services panel */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <h5 className="card-title">Add Service</h5>
          {svcError && <div className="alert alert-danger">{svcError.message}</div>}
          <form onSubmit={onSvcCreate}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="category">Category</label>
                <select
                  id="category"
                  name="category"
                  className="form-select"
                  value={svcForm.category}
                  onChange={onSvcChange}
                  required
                >
                  {activeCats.length === 0 ? (
                    <option value="">— No categories —</option>
                  ) : (
                    activeCats.map((c) => <option key={c.id} value={c.key}>{c.label}</option>)
                  )}
                </select>
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="type">Service Type (slug)</label>
                <input id="type" name="type" className="form-control" placeholder="e.g., maid-basic"
                       value={svcForm.type} onChange={onSvcChange} required />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="icon">Icon Class</label>
                <input id="icon" name="icon" className="form-control" placeholder="e.g., bi-person-lines-fill"
                       value={svcForm.icon} onChange={onSvcChange} />
                <small className="text-muted">Use Bootstrap Icons + <code>text-primary-label</code> in UI.</small>
              </div>

              <div className="col-12">
                <label className="form-label" htmlFor="title">Title</label>
                <input id="title" name="title" className="form-control"
                       value={svcForm.title} onChange={onSvcChange} required />
              </div>

              <div className="col-12">
                <label className="form-label" htmlFor="desc">Description</label>
                <textarea id="desc" name="desc" className="form-control" rows={3}
                          value={svcForm.desc} onChange={onSvcChange} />
              </div>

              <div className="col-12 col-md-4">
                <label className="form-label" htmlFor="price">Starting Price (₹)</label>
                <input id="price" name="price" type="number" min="0" step="1"
                       className="form-control" value={svcForm.price} onChange={onSvcChange} required />
              </div>

              <div className="col-12 col-md-4 d-flex align-items-center">
                <div className="form-check mt-4">
                  <input className="form-check-input" type="checkbox" id="active-svc" name="active"
                         checked={svcForm.active} onChange={onSvcChange} />
                  <label className="form-check-label" htmlFor="active-svc">Active</label>
                </div>
              </div>

              <div className="col-12">
                <button className="btn btn-primary" type="submit" disabled={svcSaving || activeCats.length === 0}>
                  {svcSaving ? "Saving..." : "Add Service"}
                </button>
                {activeCats.length === 0 && (
                  <small className="text-muted ms-2">Add a category first.</small>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Manage Services */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="card-title mb-0">Services ({svcList.length})</h5>
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Filter:</label>
              <select className="form-select form-select-sm" value={filterCat}
                      onChange={(e) => setFilterCat(e.target.value)}>
                <option value="all">All</option>
                {cats.map((c) => <option key={c.id} value={c.key}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {svcList.length === 0 ? (
            <p className="text-muted mb-0">No services yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Icon</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Active</th>
                    <th style={{ width: 180 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {svcList.map((s) => (
                    <tr key={s.id}>
                      <td><i className={`bi ${s.icon} text-primary-label`} aria-hidden="true"></i></td>
                      <td>{s.title}</td>
                      <td><code>{s.type}</code></td>
                      <td>{(cats.find(c => c.key === s.category)?.label) || s.category}</td>
                      <td>₹{s.price}</td>
                      <td>
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox"
                                 id={`svcactive-${s.id}`} checked={!!s.active}
                                 onChange={(e) => setHomeServiceActive(s.id, e.target.checked)} />
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-outline-teal btn-sm" type="button"
                                  onClick={async () => {
                                    const p = window.prompt("New price (₹):", String(s.price));
                                    if (p != null && p.trim() !== "") {
                                      await updateHomeService(s.id, { price: p });
                                    }
                                  }}>
                            Edit Price
                          </button>
                          <button className="btn btn-outline-danger btn-sm" type="button"
                                  onClick={async () => {
                                    const ok = window.confirm(`Delete "${s.title}"?`);
                                    if (ok) await deleteHomeService(s.id);
                                  }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminServices;
